/**
 * Process Form Component
 * Form for creating and editing processes
 */

import React, { useState } from 'react';
import { Process, CreateProcessRequest, UpdateProcessRequest } from '@flow-studio/api';
import { processApiClient } from '@flow-studio/api';
import { useUIStore } from '@flow-studio/store'; // import { uiStore';
import { useAuthStore } from '@flow-studio/store'; // import { authStore';
import { ReactIconPicker } from '@bizfirst/common-react';
import { FormGroup, FormLabel, FormActions, FormError, Input, Textarea, Checkbox, Button } from '../Shared/FormUI';

interface ProcessFormProps {
  process?: Process;
  projectId: number;
  onSuccess: (process: Process) => void;
  onCancel: () => void;
}

export const ProcessForm: React.FC<ProcessFormProps> = ({
  process,
  projectId,
  onSuccess,
  onCancel,
}) => {
  const { showToast } = useUIStore();
  const { getTenantId: _getTenantId } = useAuthStore();
  const isEdit = !!process;

  const [formData, setFormData] = useState({
    Name: process?.Name || '',
    Description: process?.Description || '',
    IconUrl: process?.IconUrl || '',
    ProcessTypeID: process?.ProcessTypeID || 2,
    IsTemplate: process?.IsTemplate || false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let response;
      if (isEdit) {
        // Update existing process - include TenantID and ProjectID from the existing process entity
        const updateRequest: UpdateProcessRequest = {
          ProcessID: process!.ProcessID,
          ...formData,
          ProjectID: process!.ProjectID, // Include ProjectID from server data
          TenantID: process!.TenantID, // Include TenantID from server data
        };
        response = await processApiClient.update(updateRequest);
      } else {
        const createRequest: CreateProcessRequest = {
          ...formData,
          ProjectID: projectId,
        };
        response = await processApiClient.create(createRequest);
      }

      if (response.success && response.data) {
        showToast('success', isEdit ? 'Process updated successfully!' : 'Process created successfully!');
        onSuccess(response.data);
      } else {
        const errorMessage = response.error?.message || 'Failed to save process';
        setError(errorMessage);
        showToast('error', errorMessage);
      }
    } catch (err) {
      const errorMessage = 'An error occurred while saving the process';
      setError(errorMessage);
      showToast('error', errorMessage);
      console.error('Error saving process:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && <FormError>{error}</FormError>}

      <FormGroup>
        <FormLabel required>Name</FormLabel>
        <Input type="text" value={formData.Name}
          onChange={(e) => handleChange('Name', e.target.value)}
          required placeholder="Enter process name" />
      </FormGroup>

      <FormGroup>
        <FormLabel>Description</FormLabel>
        <Textarea value={formData.Description}
          onChange={(e) => handleChange('Description', e.target.value)}
          placeholder="Process description" />
      </FormGroup>

      <FormGroup>
        <FormLabel>Icon</FormLabel>
        <ReactIconPicker value={formData.IconUrl}
          onChange={(icon) => handleChange('IconUrl', icon)} />
      </FormGroup>

      <FormGroup>
        <Checkbox id="isTemplate" label="Template"
          checked={formData.IsTemplate}
          onChange={(e) => handleChange('IsTemplate', e.target.checked)} />
      </FormGroup>

      <FormActions>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button type="submit" variant="primary" loading={loading} loadingText="Saving...">
          {isEdit ? 'Update Process' : 'Create Process'}
        </Button>
      </FormActions>
    </form>
  );
};